/**
 * Screen capture chain:
 *   1. captureViaGetDisplayMedia — native share-screen picker, one frame as PNG.
 *   2. captureViaHtml2Canvas    — DOM snapshot fallback (no WebGL/Mapbox).
 *   3. downscaleIfNeeded        — keep blobs <= 2MB / 1600px wide.
 */

export type CaptureMethod = 'display' | 'snapshot';

const MAX_BYTES = 2_000_000;
const MAX_WIDTH_PRIMARY = 1600;
const MAX_WIDTH_RETRY = 1200;
const JPEG_QUALITY = 0.8;

// Data attribute placed on the feedback modal so screenshot paths can skip it.
// html2canvas checks `ignoreElements`; getDisplayMedia hides matching elements
// in the DOM before the stream captures a frame (because the browser composits
// live DOM into the shared surface).
const HIDE_SELECTOR = '[data-feedback-overlay="true"]';

async function withModalHidden<T>(fn: () => Promise<T>): Promise<T> {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>(HIDE_SELECTOR)
  );
  const prev = nodes.map((el) => el.style.visibility);
  nodes.forEach((el) => {
    el.style.visibility = 'hidden';
  });
  // Let the browser paint the hidden state before capturing.
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
  try {
    return await fn();
  } finally {
    nodes.forEach((el, i) => {
      el.style.visibility = prev[i] ?? '';
    });
  }
}

export async function captureViaGetDisplayMedia(): Promise<Blob | null> {
  const md = navigator.mediaDevices;
  if (!md || typeof md.getDisplayMedia !== 'function') return null;

  let stream: MediaStream | null = null;
  try {
    // Request the stream FIRST (user still sees picker with modal visible — that's OK,
    // the picker blocks the page anyway). Once user confirms, hide the modal before
    // sampling a frame so the capture shows what's behind it.
    stream = await md.getDisplayMedia({ video: true });
    const track = stream.getVideoTracks()[0];
    if (!track) return null;

    return await withModalHidden(async () => {
      const video = document.createElement('video');
      video.srcObject = stream!;
      video.muted = true;
      await video.play();

      await new Promise<void>((resolve) => {
        if (video.readyState >= 2) resolve();
        else video.addEventListener('loadeddata', () => resolve(), { once: true });
      });

      // Wait a couple of frames so the composited stream reflects the hidden modal.
      await new Promise<void>((resolve) => setTimeout(resolve, 150));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1920;
      canvas.height = video.videoHeight || 1080;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      return await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png')
      );
    });
  } catch {
    return null;
  } finally {
    if (stream) stream.getTracks().forEach((t) => t.stop());
  }
}

export async function captureViaHtml2Canvas(): Promise<Blob | null> {
  try {
    const mod = await import('html2canvas');
    const html2canvas = (mod as any).default ?? mod;
    const canvas: HTMLCanvasElement = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      ignoreElements: (el: Element) =>
        el instanceof HTMLElement && el.dataset.feedbackOverlay === 'true',
    });
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png')
    );
  } catch {
    return null;
  }
}

async function rescaleBlob(blob: Blob, maxWidth: number): Promise<Blob | null> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });

    const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
    const w = Math.round(img.width * ratio);
    const h = Math.round(img.height * ratio);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);

    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY)
    );
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downscaleIfNeeded(
  blob: Blob,
  maxBytes = MAX_BYTES,
  maxWidth = MAX_WIDTH_PRIMARY
): Promise<Blob> {
  if (blob.size <= maxBytes) return blob;

  const first = await rescaleBlob(blob, maxWidth);
  if (first && first.size <= maxBytes) return first;

  const second = await rescaleBlob(first ?? blob, MAX_WIDTH_RETRY);
  return second ?? first ?? blob;
}

export async function captureScreen(method: CaptureMethod): Promise<Blob | null> {
  const raw =
    method === 'display'
      ? await captureViaGetDisplayMedia()
      : await captureViaHtml2Canvas();
  if (!raw) return null;
  return downscaleIfNeeded(raw);
}
